import { useState, useEffect } from 'react'
import { X, Ruler, Plus, Edit, Trash2, Calendar, FileText, Layers } from 'lucide-react'
import { Button, LoadingSpinner } from '../common'
import customerMeasurementService from '../../services/customerMeasurementService'
import measurementFieldConfigService from '../../services/measurementFieldConfigService'
import toast from 'react-hot-toast'

// ── Inline Add / Edit Form ──────────────────────────────────────────────────
function MeasurementForm({
  customer,
  editingMeasurement,
  garmentCategories,
  usedCategories,
  onSave,
  onCancel,
  isSubmitting,
}) {
  const [formData, setFormData] = useState({
    garmentCategory: editingMeasurement?.garmentCategory || '',
    notes: editingMeasurement?.notes || '',
  })
  const [values, setValues] = useState(editingMeasurement?.values || {})
  const [fieldConfigs, setFieldConfigs] = useState([])
  const [isFetchingFields, setIsFetchingFields] = useState(false)

  // Fetch field configs when garmentCategory changes
  useEffect(() => {
    const cat = formData.garmentCategory
    if (!cat) { setFieldConfigs([]); return }

    const fetchFields = async () => {
      setIsFetchingFields(true)
      try {
        const res = await measurementFieldConfigService.getAll({ garmentCategory: cat })
        const configs = res.data.configs || []
        setFieldConfigs(configs)
        // Keep existing values and init missing keys as empty
        setValues((prev) => {
          const next = { ...prev }
          configs.forEach((c) => {
            if (next[c.fieldKey] === undefined) next[c.fieldKey] = ''
          })
          return next
        })
      } catch {
        toast.error('Failed to load measurement fields')
      } finally {
        setIsFetchingFields(false)
      }
    }
    fetchFields()
  }, [formData.garmentCategory])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.garmentCategory) { toast.error('Please select a garment category'); return }

    const cleanValues = {}
    Object.entries(values).forEach(([k, v]) => {
      cleanValues[k] = v === '' ? null : parseFloat(v) || v
    })
    onSave({ ...formData, values: cleanValues })
  }

  return (
    <form onSubmit={handleSubmit} className="border border-secondary-200 rounded-lg p-4 bg-secondary-50 space-y-4">
      <h4 className="text-sm font-semibold text-secondary-900">
        {editingMeasurement ? 'Edit Measurement' : 'Add New Measurement'}
      </h4>

      {/* Garment category */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Garment Category *
          </label>
          {(() => {
            const availableCategories = editingMeasurement
              ? garmentCategories
              : garmentCategories.filter((cat) => !usedCategories.includes(cat))
            const allUsed = !editingMeasurement && availableCategories.length === 0 && garmentCategories.length > 0

            return (
              <>
                <select
                  className="input text-sm"
                  value={formData.garmentCategory}
                  onChange={(e) => setFormData((p) => ({ ...p, garmentCategory: e.target.value }))}
                  disabled={!!editingMeasurement || allUsed}
                >
                  <option value="">Select Category</option>
                  {availableCategories.map((cat) => (
                    <option key={cat} value={cat} className="capitalize">{cat}</option>
                  ))}
                </select>
                {garmentCategories.length === 0 && (
                  <p className="text-xs text-danger-600 mt-1">
                    No categories configured. Set up Measurement Setup first.
                  </p>
                )}
                {allUsed && (
                  <p className="text-xs text-warning-600 mt-1">
                    All categories already have a measurement for this customer.
                  </p>
                )}
                {!editingMeasurement && usedCategories.length > 0 && availableCategories.length > 0 && (
                  <p className="text-xs text-secondary-400 mt-1">
                    {usedCategories.length} categor{usedCategories.length === 1 ? 'y' : 'ies'} already recorded.
                  </p>
                )}
              </>
            )
          })()}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">Notes</label>
          <input
            className="input text-sm"
            placeholder="Optional notes..."
            value={formData.notes}
            onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
          />
        </div>
      </div>

      {/* Dynamic measurement fields */}
      {isFetchingFields ? (
        <div className="flex justify-center py-4"><LoadingSpinner /></div>
      ) : fieldConfigs.length > 0 ? (
        <div>
          <p className="text-xs text-secondary-500 mb-2 capitalize font-medium">
            {formData.garmentCategory} measurements
          </p>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {fieldConfigs.map((config) => (
              <div key={config.fieldKey}>
                <label className="block text-xs font-medium text-secondary-700 mb-1">
                  {config.label}
                  <span className="text-secondary-400 ml-1">({config.unit})</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input text-sm"
                  placeholder="0.00"
                  value={values[config.fieldKey] ?? ''}
                  onChange={(e) =>
                    setValues((p) => ({ ...p, [config.fieldKey]: e.target.value }))
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ) : formData.garmentCategory ? (
        <p className="text-xs text-secondary-400 italic">
          No fields configured for "{formData.garmentCategory}". Edit in Measurement Setup.
        </p>
      ) : null}

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm" loading={isSubmitting} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : editingMeasurement ? 'Update' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

// ── Main Modal ──────────────────────────────────────────────────────────────
function CustomerNewMeasurementsModal({ isOpen, customer, onClose, userRole }) {
  const [measurements, setMeasurements] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [garmentCategories, setGarmentCategories] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingMeasurement, setEditingMeasurement] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen && customer?.id) {
      fetchMeasurements()
      fetchCategories()
    }
    if (!isOpen) {
      setShowForm(false)
      setEditingMeasurement(null)
    }
  }, [isOpen, customer?.id])

  const fetchMeasurements = async () => {
    setIsLoading(true)
    try {
      const res = await customerMeasurementService.getByCustomer(customer.id)
      setMeasurements(res.data.measurements || [])
    } catch {
      toast.error('Failed to fetch measurements')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const res = await measurementFieldConfigService.getGarmentCategories()
      setGarmentCategories(res.data.categories || [])
    } catch {}
  }

  const handleSave = async (data) => {
    setIsSubmitting(true)
    try {
      if (editingMeasurement) {
        await customerMeasurementService.update(editingMeasurement.id, {
          values: data.values,
          notes: data.notes,
        })
        toast.success('Measurement updated!')
      } else {
        await customerMeasurementService.create({
          customerId: customer.id,
          garmentCategory: data.garmentCategory,
          values: data.values,
          notes: data.notes,
        })
        toast.success('Measurement added!')
      }
      setShowForm(false)
      setEditingMeasurement(null)
      await fetchMeasurements()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save measurement')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (measurement) => {
    if (!window.confirm('Delete this measurement?')) return
    try {
      await customerMeasurementService.delete(measurement.id)
      toast.success('Measurement deleted!')
      await fetchMeasurements()
    } catch {
      toast.error('Failed to delete measurement')
    }
  }

  const handleAddClick = () => {
    setEditingMeasurement(null)
    setShowForm(true)
  }

  const handleEditClick = (measurement) => {
    setEditingMeasurement(measurement)
    setShowForm(true)
  }

  const handleCancelForm = () => {
    setShowForm(false)
    setEditingMeasurement(null)
  }

  if (!isOpen || !customer) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-white px-6 pt-5 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-primary-100">
                  <Layers className="h-5 w-5 text-primary-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-secondary-900">
                    Dynamic Measurements — {customer.firstName} {customer.lastName}
                  </h3>
                  <p className="text-sm text-secondary-500">
                    Measurements configured per garment category
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!showForm && (
                  <Button variant="primary" size="sm" onClick={handleAddClick}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                )}
                <button
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Inline form */}
            {showForm && (
              <div className="mb-4">
                <MeasurementForm
                  customer={customer}
                  editingMeasurement={editingMeasurement}
                  garmentCategories={garmentCategories}
                  usedCategories={measurements.map((m) => m.garmentCategory)}
                  onSave={handleSave}
                  onCancel={handleCancelForm}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}

            {/* Measurements list */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <LoadingSpinner />
                </div>
              ) : measurements.length === 0 ? (
                <div className="text-center py-10">
                  <Layers className="mx-auto h-10 w-10 text-secondary-300 mb-3" />
                  <p className="text-sm font-medium text-secondary-900">No measurements yet</p>
                  <p className="text-xs text-secondary-500 mt-1">
                    Click "Add" to record a measurement
                  </p>
                </div>
              ) : (
                <table className="table w-full text-sm">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Category</th>
                      <th className="table-header-cell">Measurements</th>
                      <th className="table-header-cell">Notes</th>
                      <th className="table-header-cell">Date</th>
                      <th className="table-header-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {measurements.map((m) => {
                      const entries = Object.entries(m.values || {}).filter(
                        ([, v]) => v !== null && v !== ''
                      )
                      const preview = entries.slice(0, 4)

                      return (
                        <tr key={m.id} className="table-row">
                          {/* Category */}
                          <td className="table-cell">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 capitalize">
                              {m.garmentCategory}
                            </span>
                          </td>

                          {/* Values preview */}
                          <td className="table-cell">
                            {preview.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {preview.map(([k, v]) => (
                                  <span
                                    key={k}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700"
                                  >
                                    {k}: {v}
                                  </span>
                                ))}
                                {entries.length > 4 && (
                                  <span className="text-xs text-secondary-400">
                                    +{entries.length - 4} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-secondary-400 text-xs">—</span>
                            )}
                          </td>

                          {/* Notes */}
                          <td className="table-cell max-w-xs">
                            {m.notes ? (
                              <div className="flex items-start text-xs text-secondary-600">
                                <FileText className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                                <span className="truncate">{m.notes}</span>
                              </div>
                            ) : (
                              <span className="text-secondary-400 text-xs">—</span>
                            )}
                          </td>

                          {/* Date */}
                          <td className="table-cell">
                            <div className="flex items-center text-xs text-secondary-500">
                              <Calendar className="h-3 w-3 mr-1" />
                              {new Date(m.createdAt).toLocaleDateString()}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="table-cell">
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleEditClick(m)}
                                className="text-primary-600 hover:text-primary-900"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              {userRole === 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleDelete(m)}
                                  className="text-danger-600 hover:text-danger-900"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerNewMeasurementsModal
